import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import moment from 'moment-business-days';
import { Option } from 'antd/lib/mentions';
import { message, Modal } from 'antd';
import ordinal from 'ordinal';

// Components
import { Text, Tag, TagV2 } from '../../../../Reusable/Text';
import { Image } from '../../../../Reusable/Image';
import { FlexColumn, Flex } from '../../../../Reusable/Container';
import { ErrorLabel, LabeledInput, StyledInstrLabel } from '../../../../Reusable/Input';
import { ErrorAlert } from '../../../../Reusable/Alert';
import { TextButton, ImageButton, Button } from '../../../../Reusable/Button';
import { ApprovalGraph } from '../../../../Reusable/Approval';
import PaymentDetails from '../../../../Reusable/PaymentDetails';
import { FromAccountOption, PaymentMethodOption, PaymentMethodOptionV2, railCards } from '../../../../Reusable/Option';
import NewInboxVendor from '../../../Vendors/Create Vendor/NewInboxVendor';
import AddPaymentMethod from '../../../Vendors/AddPaymentMethod';

import environment from '../../../../../environment';
import { apiPOSTReq } from '../../../../../Utils/api';
import {
    dateFormatList,
    getDateInLocalTZ,
    toAbsCurrency,
    toNumberStr,
    toCurrency,
    isBeforeCutOff,
    getBPAccountsWithAccess,
    renderBankIcon,
    getRailPaymentInfo,
    railCutOffTxt
} from '../../../../../Utils/util';

// Images
import AionIcon from '../../../../../Images/aion-bank-icon.png';
import ModalClose from '../../../../../Images/modal-close.png';
import Edit from "../../../../../Images/edit.svg";
import Add from '../../../../../Images/add.svg';
import { LucidePlus } from 'lucide-react';
import hexToRgba from 'hex-to-rgba';
import ToolTip from '../../../../Reusable/Tooltip';


class PayCard extends Component {
    state = {
        payOnOption: "Select Date",
        messageLoading: true,
        paymentMethods: [],
        accounts: [],
        newPM: {},
        vendor: {},
        accountCOA: {},
        pairPagination: {
            current: 1,
            pageSize: 1000,
        },
        allAccountsPaired: true,
        paymentAmount: null,
        scheduledPayments: this.props.scheduledPayments,
        scheduledAmt: 0,
        showPaymentModal: false,
        payBillLoading: false,
        errorField: null,
        errorMessage: null,
        submitLoading: false,
        showPaymentMemo: true,
        expandApprovals: false
    };

    componentDidMount() {
        const { bill, store, vendor, billBalance } = this.props;
        var { publicHolidays } = store;

        moment.updateLocale('us', {
            holidays: publicHolidays,
            holidayFormat: 'YYYY-MM-DD'
        });

        var accounts = getBPAccountsWithAccess(null, { resourceStr: "Payables.Bills", permType: "manage", getActiveAccounts: true, ignoreARAcct: true }) || [];
        accounts = accounts.filter(item => { return (!['Transitional', 'CHECKINGYIELD'].includes(item.accountType)) }) || [];
        var fromAccount = {};
        if (accounts.length > 0) {
            fromAccount = accounts[0];
        }

        this.setState({
            paymentAmount: toNumberStr(billBalance, 2),
            paymentDate: this.getInitialPaymentDate(bill.dueDate),
            accounts: accounts,
            fromAccount: fromAccount,
            bill: bill,
            vendor: vendor
        }, () => {
            this.evaluatePaymentApproval(billBalance);
            // Get payment methods after state is set
            this.getPaymentMethods({ lastUsedPMId: vendor?.lastUsedPMId, lastUsedAccount: vendor?.lastUsedAccount ? accounts.find(account => account.accountNumber === vendor?.lastUsedAccount) : null });
            this.getSchldPmts();
        });

        this.getTransferLimit('Instant Transfer');
        this.getTransferLimit('ACH');
        this.getTransferLimit('Wire');
    }

    componentDidUpdate(prevProps) {
        // Handle bill updates
        if (prevProps.bill !== this.props.bill) {
            const { bill } = this.props;

            // Update bill in state
            this.setState({
                bill: bill,
                // Also update payment amount if bill balance has changed
                paymentAmount: bill && bill.balance !== this.state.paymentAmount ?
                    toNumberStr(this.props.billBalance, 2) :
                    this.state.paymentAmount
            });

            // If bill due date has changed, update payment date
            if (bill && prevProps.bill?.dueDate !== bill.dueDate) {
                this.setState({
                    paymentDate: this.getInitialPaymentDate(bill.dueDate)
                });
            }

            // If bill ID has changed, we need to update scheduled payments
            if (bill && prevProps.bill?.id !== bill.id) {
                this.getSchldPmts(true);
            }
        }

        if(prevProps.billBalance != this.props.billBalance) {
            this.setState({ billBalance: this.props.billBalance }, () => this.evaluatePaymentApproval(this.props.billBalance))
        }

        // Handle vendor updates
        if (prevProps.vendor !== this.props.vendor) {
            const { vendor } = this.props;

            this.setState({
                vendor: vendor
            }, () => {
                // If vendor has changed, we need to update payment methods
                if (vendor) {
                    this.getPaymentMethods({
                        lastUsedPMId: vendor.lastUsedPMId,
                        lastUsedAccount: vendor.lastUsedAccount ?
                            this.state.accounts.find(account => account.accountNumber === vendor.lastUsedAccount) :
                            null
                    });
                }
            });
        }

        // Handle store updates (for public holidays)
        if (prevProps.store?.publicHolidays !== this.props.store?.publicHolidays) {
            const { publicHolidays } = this.props.store || {};

            // Update moment locale with new holidays
            if (publicHolidays) {
                moment.updateLocale('us', {
                    holidays: publicHolidays,
                    holidayFormat: 'YYYY-MM-DD'
                });

                // Also refresh payment date based on new holidays
                this.setState({
                    paymentDate: this.state.paymentDate ?
                        this.getInitialPaymentDate(this.state.bill?.dueDate) :
                        this.state.paymentDate
                });
            }
        }

        // Handle payment mode toggle
        if (prevProps.payMode !== this.props.payMode) {
            // Update any UI state that depends on payment mode
            if (this.props.payMode) {
                // When entering pay mode, refresh data
                this.getSchldPmts();

                // If we have a selected payment method, refresh limits
                if (this.state.selectedPM) {
                    this.getDailyLimits(this.state.selectedPM);
                    this.getFees({
                        ...this.state.selectedPM,
                        fromAccount: this.state.fromAccount || {}
                    });
                }
            }
        }

        // Handle accounts change (from banking store updates)
        if (prevProps.bankingStore?.accounts !== this.props.bankingStore?.accounts) {
            const accounts = getBPAccountsWithAccess(null, {
                resourceStr: "Payables.Bills",
                permType: "manage",
                getActiveAccounts: true,
                ignoreARAcct: true
            }) || [];

            // Filter accounts
            const filteredAccounts = accounts.filter(item => {
                return (!['Transitional', 'CHECKINGYIELD'].includes(item.accountType))
            }) || [];

            // Update accounts and default fromAccount if needed
            this.setState({
                accounts: filteredAccounts,
                fromAccount: this.state.fromAccount ||
                    (filteredAccounts.length > 0 ? filteredAccounts[0] : {})
            });
        }
    }

    getInitialPaymentDate = (dueDate) => {
        if (moment(dueDate).isBefore(moment())) {
            return moment().isBusinessDay ? moment().businessAdd(0) : moment().businessAdd(1);
        } else {
            return moment(dueDate);
        }
    };

    handleDateChange = (value) => {
        if (value === "Today") {
            var paymentDate = moment();
            this.setState({ payOnOption: value, paymentDate });
        } else {
            const { bill } = this.state;
            this.setState({ payOnOption: value, paymentDate: this.getInitialPaymentDate(bill.dueDate) });
        }
    };

    disabledDate = (current) => {
        return !current.isBusinessDay() || current < moment().add(0, 'd').startOf('day');
    };

    saveInput = (id, dataToSave) => {
        this.setState({ [id]: dataToSave, errorField: null, errorMessage: null, errorMessageComp: null }, () => {
            if (id === 'paymentAmount') this.evaluatePaymentApproval(dataToSave);
        });
    };

    evaluatePaymentApproval = (amt) => {
        const { vendor } = this.state;

        const body = {
            paymentAmount: amt,
            vendorName: vendor?.vendorName,
        };

        apiPOSTReq(`${environment.payBaseUrl}/payables/evaluatePaymentApproval`, { "BankProvider": environment.bankProvider.crb }, body, (err, resp) => {
            this.setState({ messageLoading: false, payBillLoading: false });
            try {
                const data = resp;
                if (data.result) {
                    this.setState({ approvalRequired: data.approvalRequired, approvalRule: data.approvalRule });
                } else {
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later");
                }
            } catch (error) {
                console.log("ERRR /payables/evaluatePaymentApproval", error, err, resp);
            }
        });
    };

    getApprovalText = () => {
        const { approvalRule } = this.state;
        const { levels } = approvalRule;

        var approvalText;

        if (levels.length > 1) {
            approvalRule.levels.forEach((level, i) => {
                approvalText = approvalText + `${(i > 0) && ', followed by'} <b>${ordinal(i + 1)}</b> level approval from ${this.getApproversText(level.performers, level.type)}`;
            });
        } else {
            approvalText = `This payment requires approval from ${this.getApproversText(levels[0].performers, levels[0].type)}`;
        }

        return <Text>{approvalText}</Text>;
    };

    getApproversText = (approvers, approvalBy) => {
        if (approvers.length == 1) {
            return `${approvers[0].firstName} ${approvers[0].lastName}`;
        } else {
            return (
                <>
                    {approvers.map(a => `${a.firstName} ${a.lastName}`).slice(0, approvers.length - 1).join(", ")}
                    <b>{approvalBy === "ANY" ? " or " : " and "}</b>
                    {`${approvers[approvers.length - 1].firstName} ${approvers[approvers.length - 1].lastName}`}
                </>
            );
        }
    };

    getSchldPmts = (updatePmnt = false) => {
        this.props.getSchldPmts(this.state.bill?.id);
        // var { bill, paymentAmount } = this.state;
        // // This includes both pending approval and scheduled statuses
        // var body = {
        //     "billId": bill.id,
        //     "viewBy": "APPROVAL_SCHEDULED"
        // };

        // apiPOSTReq(`${environment.payBaseUrl}/payables/getPayments`, { "BankProvider": environment.bankProvider.crb }, body, (err, resp) => {
        //     this.setState({ submitLoading: false, payBillLoading: false });
        //     try {
        //         const data = resp || {};
        //         if (data.result) {
        //             if (updatePmnt) paymentAmount = toNumberStr((bill || {}).balance, 2);
        //             var scheduledPayments = data.achTransfersList || [];
        //             scheduledPayments = scheduledPayments.concat(data.wireTransfersList || []);
        //             scheduledPayments = scheduledPayments.concat(data.instantTransfersList || []);
        //             var scheduledAmt = 0;
        //             scheduledPayments.forEach(pmnt => {
        //                 scheduledAmt += parseFloat(pmnt.amount);
        //             });
        //             var pAmt = toNumberStr(paymentAmount - scheduledAmt, 2);
        //             if (pAmt == 0 && !updatePmnt) pAmt = null;
        //             this.setState({ scheduledPayments: scheduledPayments, paymentAmount: pAmt, scheduledAmt: scheduledAmt });
        //             if (!updatePmnt) this.evaluatePaymentApproval(pAmt);
        //         } else {
        //             throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later");
        //         }
        //     } catch (error) {
        //         console.log("PayBill getSchldPmts ERRR ", error, err, resp);
        //     }
        // });
    };

    pay = async () => {
        const { bankingStore, store, onPay, saveBill } = this.props;
        var { payOnOption, paymentDate, approvalRequired, note, fromAccount, bill, vendor, paymentMethods, selectedPM, paymentAmount, ACHTransferLimit, WireTransferLimit, InstantTransferLimit } = this.state;

        const { UserInfo } = store;

        if (!paymentAmount || (paymentAmount == 0)) {
            this.setState({ errorField: "paymentAmount", errorMessage: "Enter a valid amount", payBillLoading: false });
            return;
        }

        if (paymentAmount > bill.amount) {
            this.setState({ errorField: "paymentAmount", errorMessage: "Amount is greater than bill amount", payBillLoading: false });
            return;
        }

        if (!fromAccount) {
            this.setState({ errorField: "fromAccount", payBillLoading: false });
            return;
        }

        this.setState({ submitLoading: true, payBillLoading: true });

        if (paymentMethods.length == 0) {
            var pmResp = await this.savePaymentMethod();
            if (pmResp) {
                selectedPM = pmResp.paymentMethod;
                vendor = pmResp.vendor;
            } else {
                return; // savePaymentMethod will throw error
            }
        }

        if (WireTransferLimit && (selectedPM?.type === 'Wire' && paymentAmount > Number(WireTransferLimit))) {
            this.setState({ errorField: 'paymentAmount', errorMessageComp: <ErrorLabel>Exceeded wire transfer limit of <span style={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '12px' }}>${toCurrency(WireTransferLimit)}</span></ErrorLabel>, payBillLoading: false, submitLoading: false });
            return;
        }

        if (ACHTransferLimit && (selectedPM?.type === 'ACH' && paymentAmount > Number(ACHTransferLimit))) {
            this.setState({ errorField: 'paymentAmount', errorMessage: <ErrorLabel>Exceeded ACH transfer limit of <span style={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '12px' }}>${toCurrency(ACHTransferLimit)}</span></ErrorLabel>, payBillLoading: false, submitLoading: false });
            return;
        }

        if (InstantTransferLimit && (selectedPM?.type === 'Instant' && paymentAmount > Number(InstantTransferLimit))) {
            this.setState({ errorField: 'paymentAmount', errorMessage: <ErrorLabel>Exceeded instant transfer limit of <span style={{ cursor: 'pointer', textDecoration: 'underline', fontSize: '12px' }}>${toCurrency(InstantTransferLimit)}</span></ErrorLabel>, payBillLoading: false, submitLoading: false });
            return;
        }

        var body = {
            "billId": bill.id,
            "paymentDesc": bill.paymentDesc,
            "paymentMemo": bill.paymentMemo,
            "vendorId": vendor.id,
            "paymentMethodId": selectedPM.id,
            "initiatedBy": `${UserInfo.FirstName} ${UserInfo.LastName}`,
            "ruleType": approvalRequired ? "Payable" : null,
            "contextIdentifier": `Bill Payment - ${vendor.vendorName}: ${bill.invoiceNumber}`,
            "accountNumber": fromAccount.accountNumber,
            "note": note,
            "paymentAmount": paymentAmount,
            "scheduleDate": moment(paymentDate).format("YYYY-MM-DD")
        };

        var payEndpoint = this.isPaymentDateToday() ? "pay" : "schedule";
        if (payEndpoint === "schedule") {
            body.contextIdentifier = `Bill Payment - ${vendor.vendorName}: ${bill.invoiceNumber}`;
        }

        apiPOSTReq(`${environment.payBaseUrl}/payables/${payEndpoint}`, { "BankProvider": environment.bankProvider.crb }, body, (err, resp) => {
            try {
                const data = resp || {};
                if (data.result) {
                    if (onPay) {
                        onPay((bill) => {
                            this.setState({ submitLoading: false, payBillLoading: false });
                            message.success("Payment sent out successfully!");
                            this.setState({ bill });
                        });
                    }
                } else {
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later");
                }
            } catch (error) {
                this.setState({ submitLoading: false, payBillLoading: false });
                console.log("ERRR /payables/pay", error, err, resp);
                ErrorAlert({ description: error.message });
            }
        });
    };

    saveBill = (callback) => {
        const { saveBill } = this.props;
        var { bill, vendor } = this.state;

        this.setState({ payBillLoading: true });
        if (saveBill) {
            saveBill({ bill }, callback);
        } else {
            // If saveBill prop not provided, just call the callback
            if (callback) callback();
        }
    };

    savePaymentMethod = (options) => {
        var { newPM, vendor, fromAccount } = this.state;
        const { accountNumber, routingNumber } = newPM;

        return new Promise((resolve) => {
            if ((!accountNumber || isNaN(accountNumber))) {
                this.setState({ errorField: "accountNumber", payBillLoading: false });
                resolve(null);
                return;
            }

            if ((!routingNumber || isNaN(routingNumber) || routingNumber.length != 9)) {
                this.setState({ errorField: "routingNumber", payBillLoading: false });
                resolve(null);
                return;
            }

            var body = {
                vendorDeliveryMode: 'ACH',
                vendorId: vendor.id,
                accountId: fromAccount.accountId,
                nickName: vendor.vendorName,
                vendorType: vendor.type,
                bankDetail: {
                    accountNumber: accountNumber || "",
                    routingNumber: routingNumber || "",
                    accountType: "checking",
                },
            };

            apiPOSTReq(`${environment.payBaseUrl}/payables/vendors/addTransferDelivery`, { "BankProvider": environment.bankProvider.crb }, body, (err, resp) => {
                this.setState({ createVendorLoading: false });
                try {
                    if (err) throw Error(err);
                    const data = resp || {};
                    if (data.result) {
                        resolve(data);
                    } else {
                        throw Error(data.responseMessage || data.error);
                    }
                } catch (error) {
                    console.log("/createCounterparty err", error, resp, body);
                    ErrorAlert({ description: error.message || "Sorry, we had trouble processing your request. Please try again." });
                    resolve(null);
                }
                this.setState({ submitLoading: false });
            });
        });
    };

    getPaymentMethods = (options) => {
        var { preselect, lastUsedPMId, lastUsedAccount } = options || {};
        var { vendor, fromAccount } = this.state;

        if (lastUsedAccount) fromAccount = lastUsedAccount;

        const body = {
            "vendorId": vendor?.associatedCPObjectId
        };

        this.setState({ paymentMethodsLoading: true });

        apiPOSTReq(`${environment.payBaseUrl}/payables/vendors/getPaymentMethods`, { "BankProvider": environment.bankProvider.crb }, body, (err, resp) => {
            try {
                const data = resp;
                if (data.result) {
                    var selectedPM;
                    var paymentMethods = data.paymentMethods || [];

                    if (lastUsedPMId) {
                        selectedPM = paymentMethods.find(pm => pm.id == lastUsedPMId);
                    } else {
                        selectedPM = (preselect && paymentMethods.length > 0) 
                                        ? 
                                            (paymentMethods[paymentMethods.length - 1])
                                        : 
                                            ((paymentMethods || []).find(pm => pm.type === "Instant") || (paymentMethods || []).find(account => account.default == true));
                    }

                    console.log("paycard selectedPM", paymentMethods, selectedPM)

                    this.setState({ paymentMethods: paymentMethods, selectedPM }, () => {
                        if (selectedPM) this.getDailyLimits(selectedPM);
                        if (fromAccount) this.getFees({ ...(selectedPM || {}), fromAccount: fromAccount || {} });
                    });
                }
            } catch (error) {
                console.log("PayBill getPaymentMethods", error, resp);
            } finally {
                this.setState({ paymentMethodsLoading: false });
            }
        });
    };

    handleAccountSelect = (value, option) => {
        const { selectedPM } = this.state;
        var fromAccount = this.state.accounts.find(account => account.accountId === value);

        this.setState({ fromAccount }, () => {
            // Fetch daily limits
            this.getDailyLimits(selectedPM);
            this.getFees({ ...selectedPM, fromAccount });
        });
    };

    handleSelectpaymentMethod = (value, option) => {
        const { paymentMethods, bill } = this.state;
        var { paymentMemo, paymentDesc } = bill;

        var paymentMethod = paymentMethods.find(pm => pm.id === value);

        if (paymentMethod?.type === 'ACH') {
            if (paymentMemo) bill.paymentMemo = paymentMemo.slice(0, 80);
            if (paymentDesc) bill.paymentDesc = paymentDesc.slice(0, 10);
            this.setState({ bill });
        }

        this.setState({ selectedPM: paymentMethod, errorField: null }, () => {
            // Fetch daily limits
            this.getDailyLimits(paymentMethod);
            this.getFees({ ...paymentMethod, fromAccount: this.state.fromAccount || {} });
        });
    };

    isPaymentDateToday = () => {
        var { paymentDate } = this.state;
        return (moment(paymentDate).format(environment.defaultDateFormat) === moment().format(environment.defaultDateFormat));
    };

    getDeliveryDate(paymentDate) {
        var { paymentDate } = this.state;
        if (this.isPaymentDateToday()) {
            return moment(paymentDate).businessAdd(isBeforeCutOff() ? 1 : 2).format(environment.defaultDateFormat);
        } else {
            return moment(paymentDate).businessAdd(1).format(environment.defaultDateFormat);
        }
    };

    getDailyLimits = (options) => {
        const { fromAccount, selectedPM } = this.state;
        options = options || {};
        var paymentType = selectedPM?.type || 'ACH';
        var url;
        var body = {
            "wireTransferObj": {
                "Amount": options.amount || "0.00"
            },
            "achTransferObj": {
                "Amount": options.amount || "0.00"
            },
            "checkPaymentObj": {
                "Amount": options.amount || "0.00"
            },
            "bookTransferObj": {
                "Amount": options.amount || "0.00"
            },
            "instantTransferObj": {
                "Amount": options.amount || "0.00"
            }
        };

        if (paymentType == "ACH") {
            if (fromAccount?.accountNumber) {
                body.accountNumber = fromAccount?.accountNumber;
            }
            url = `${environment.bbBaseUrl}/bb/getDailyLimitsACH`;
        } else if (paymentType == "Wire") {
            if (fromAccount?.accountNumber) {
                body.accountNumber = fromAccount?.accountNumber;
            }
            url = `${environment.bbBaseUrl}/bb/getDailyLimitsWire`;
        } else if (paymentType == "Check") {
            url = `${environment.bbBaseUrl}/bb/getDailyLimitsCheck`;
        } else if (paymentType == "Instant") {
            if (fromAccount && fromAccount.accountId) {
                body.bookTransferObj.fromAccountId = fromAccount.accountId;
            }
            url = `${environment.bbBaseUrl}/bb/getDailyLimitsInstant`;
        } else {
            return;
        }

        apiPOSTReq(url, null, body, (err, resp) => {
            try {
                const data = resp || {};
                if (data.result) {
                    if (err) throw new Error(err);
                    this.setState({ dailyLimits: data || {} });
                } else {
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later");
                }
            } catch (error) {
                console.log("ERRR getDailyLimits", error, err, resp);
            }
        });
    };

    getFees = (options) => {
        options = options || {};
        var { fromAccount, type } = options;
        var feeType;

        feeType = (type == "Wire") ? "DomesticWireOut" : "OutgoingACH";
        if (type == "Check") feeType = "DomesticCheckPayment";
        if (type == "Instant") feeType = "InstantTransfer";

        var body = {
            "feeType": feeType
        };

        if ((fromAccount || {}).accountNumber) {
            body.accountNumber = fromAccount.accountNumber;
        }

        apiPOSTReq(`${environment.bbBaseUrl}/bb/getFeeInfo`, null, body, (err, resp) => {
            try {
                const data = resp || {};
                if (data.result) {
                    if (err) throw new Error(err);
                    this.setState({ feeInfo: data || {} });
                } else {
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later");
                }
            } catch (error) {
                console.log("ERRR getFeeInfo", error, err, resp);
            }
        });
    };

    getTransferLimit = (transferMethod) => {
        var body = {
            transferMethod: transferMethod,
        };

        apiPOSTReq(`${environment.bbBaseUrl}/bb/getTransferLimits`, null, body, (err, resp) => {
            try {
                if (err) throw new Error(err);
                const data = resp || {};

                if (data.result) {
                    this.setState({ [`${transferMethod === "Instant Transfer" ? "Instant" : transferMethod}TransferLimit`]: data.featureValue });
                } else {
                    throw Error(data.error || data.responseMessage || "Sorry we had trouble processing your request. Please try again later");
                }
            } catch (error) {
                console.log("ERRR getTransferLimits", error, err, resp);
            }
        });
    };

    handleBillTextChange = (event) => {
        var { bill } = this.state;
        bill[event.target.id] = event.target.value;
        this.setState({ bill });
    };

    render() {
        const { theme, billAmtPaid } = this.props;
        const {
            bill, vendor, paymentDate, approvalRequired, approvalRule, payBillLoading, selectedPM, paymentMethodsLoading,
            paymentMethods, fromAccount, errorField, accounts, newPM, paymentAmount, scheduledPayments, scheduledAmt,
            dailyLimits, feeInfo, errorMessage, submitLoading, showPaymentMemo, errorMessageComp, expandApprovals
        } = this.state;

        if (!bill) return null;

        const { invoiceNumber, amount, balance, dueDate, paymentMemo, paymentDesc, payments, status } = bill;

        // Determine whether the bill is unpaid and if there's a partial payment
        const unpaid = !billAmtPaid;

        // Calculate total payments made
        let paymentsTotal = 0;
        const paidpayments = payments ? payments.filter(pmt => ["Initiated", "Pending", "Processing", "Paid", "On Hold", "Compliance Alert", "Completed"].includes(pmt.paymentStatus)) : [];
        paidpayments.forEach(pmnt => { paymentsTotal += parseFloat(pmnt.amount) });

        // Determine if there's a partial payment
        const partialPayment = ((paidpayments || []).length > 0 && balance > 0);

        var deliveryMode = selectedPM?.type;

        // Set up account options
        const accountOptions = accounts.map(account => (
            <Option key={account.accountId} value={account.accountId} style={{ backgroundColor: "transparent" }}>
                <FromAccountOption account={account} />
            </Option>
        ));

        // Set up payment method options
        const nickNameMaxLen = 8;
        const paymentMethodOptions = paymentMethods.map(paymentMethod => (
            <Option key={paymentMethod.id} value={paymentMethod.id} style={{ backgroundColor: "transparent", width: '100%' }}>
                <PaymentMethodOptionV2 paymentMethod={paymentMethod} rail={paymentMethod.type} />
            </Option>
        ));

        // Set memo and identifier max lengths based on delivery mode
        const memoMaxLength = deliveryMode === 'Instant' ? 140 : 80;
        const identifierMaxLength = deliveryMode === 'Instant' ? 50 : 10;

        var railCard = railCards.find(rc => rc.name == selectedPM?.type) || {};

        var fromAccountNickName = `${(selectedPM?.nickName || selectedPM?.type || "").trim().substr(0, nickNameMaxLen)}${(selectedPM?.nickName || selectedPM?.type || "").trim().length > nickNameMaxLen ? ".." : ""}`
        const acctChars = (selectedPM?.bankDetail?.accountNumber || "").length;

        const methodDetails = getRailPaymentInfo(dailyLimits, feeInfo, null);
        const pmFeeInfo = methodDetails[selectedPM?.type] || {};

        return (
            <FlexColumn start fullWidth grow>
                <FlexColumn between fullHeight>
                    <FlexColumn start style={{ padding: '24px' }} gap="24px" fullWidth>
                        {/* Header section with conditional logic */}
                        {unpaid ?
                            <Text size='25px' weight='600' height='40px'>
                                {partialPayment ? "Partially settled. Submit additional payment." : "Verify and complete payment"}
                            </Text>
                            :
                            null
                        }

                        <FlexColumn start gap="24px">
                            {/* Payment Form */}
                            <>
                                <Flex gap='24px'>
                                    <LabeledInput
                                        label={partialPayment ? "New payment amount" : "Payment amount"}
                                        nomargin
                                        id="paymentAmount"
                                        type="amount"
                                        placeholder="Enter an amount"
                                        value={unpaid && paymentAmount}
                                        onChange={value => this.saveInput("paymentAmount", value)}
                                        error={errorField === "paymentAmount"}
                                        errorMessage={errorMessageComp ?
                                            <PaymentDetails
                                                paymentMethod={selectedPM}
                                                dailyLimits={dailyLimits}
                                                feeInfo={feeInfo}
                                                sameDay={false}
                                                samedayACHFee={null}
                                                theme={theme}
                                                width="100%"
                                                limitCta={errorMessageComp}
                                            />
                                            : errorMessage}
                                    />

                                    <LabeledInput
                                        label="Initiate payment on"
                                        key="paymentDate"
                                        id="paymentDate"
                                        type="date-picker"
                                        nomargin
                                        format={dateFormatList}
                                        value={paymentDate && moment(paymentDate)}
                                        disabledDate={this.disabledDate}
                                        placeholder={moment(dueDate).format("MM-DD-YYYY")}
                                        onChange={(date, dateStr) => this.saveInput("paymentDate", date)}
                                    />
                                </Flex>

                                <Flex gap='24px'>
                                    <LabeledInput
                                        label="Account to pay from"
                                        showAction={'focus'}
                                        type="select"
                                        nomargin
                                        key="fromAccount"
                                        value={fromAccount && <><img width='16px' height='16px' src={AionIcon} style={{ marginRight: 8, marginBottom: 2 }} /><Text>{`${(fromAccount.nickName || `Business ${fromAccount.accountSubType}`)} • ${fromAccount.mask}`}</Text></>}
                                        // instruction="We will use this account for your payments to this vendor"
                                        placeholder="From account"
                                        onChange={this.handleAccountSelect}
                                        errorField={errorField}
                                        error={errorField === "fromAccount"}
                                        errorMessage="Select a from account"
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        className='parent-node-select-dropdown-menu'
                                    >
                                        {accountOptions}
                                    </LabeledInput>

                                    {/* Payment Methods Dropdown */}
                                    {paymentMethods.length > 0 && (
                                        <LabeledInput
                                            label="Vendor account to pay to"
                                            type="select"
                                            nomargin
                                            id="paymentMethod"
                                            placeholder={"Select to account"}
                                            instruction={
                                                <Flex centerH gap='4px'>
                                                    <StyledInstrLabel>Transfer Fee: <b>${toCurrency(pmFeeInfo.fee)}</b></StyledInstrLabel>
                                                    <Flex centerH gap='4px'>
                                                        <StyledInstrLabel>
                                                        |  Cut-off: <b>{pmFeeInfo.cutoff}</b>
                                                        </StyledInstrLabel>
                                                        <ToolTip desc={railCutOffTxt(selectedPM, pmFeeInfo)} />
                                                    </Flex>
                                                </Flex>
                                            }
                                            value={
                                                (selectedPM && selectedPM.bankDetail) &&
                                                <Flex between centerHorizontally style={{ height: '100%', width: '100%' }}>
                                                    <Flex start centerHorizontally>
                                                        {renderBankIcon(selectedPM.bankDetail)}
                                                        <Text title={selectedPM.nickName}>
                                                            {selectedPM.type == "Check"
                                                                ? `${fromAccountNickName} • ${selectedPM.bankDetail.checkDeliveryType == 'PostalMail' ? 'Postal Mail' : selectedPM.bankDetail.checkDeliveryType}`
                                                                : `${fromAccountNickName} • ${(selectedPM.bankDetail.accountNumber).substr(acctChars-4, acctChars)}`
                                                            }
                                                        </Text>
                                                    </Flex>
                                                    <Flex start centerHorizontally gap='4px'>
                                                        <TagV2 style={{ height: '20px' }} centerH color={theme.colors.primary}>{railCard.name}</TagV2>
                                                        <TagV2 style={{ height: '20px' }} centerH gap='4px' title={railCard.desc}>
                                                            {railCard.icon}
                                                            {railCard.tag}
                                                        </TagV2>
                                                    </Flex>
                                                    
                                                </Flex>
                                            }
                                            loading={paymentMethodsLoading}
                                            onChange={this.handleSelectpaymentMethod}
                                            dropdownRender={menu => (
                                                <FlexColumn start left>
                                                    {menu}
                                                    <TextButton margin='8px 16px' onClick={() => this.setState({ showPaymentModal: true })} text='ADD NEW' rightIcon={<Image src={Add} />} />
                                                </FlexColumn>
                                            )}
                                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                            className='parent-node-select-dropdown-menu'
                                        >
                                            {paymentMethodOptions}
                                        </LabeledInput>
                                    )}
                                </Flex>

                                {/* Add a new payment method if none exist */}
                                {paymentMethods.length === 0 && (
                                    <NewInboxVendor
                                        payMode
                                        key='pm'
                                        vendor={vendor}
                                        pm={newPM}
                                        handlePMChange={(pm) => this.setState({ newPM: pm })}
                                        errorField={errorField}
                                        addPaymentMethod={() => this.setState({ showPaymentModal: true })}
                                        handleVendorChange={() => this.getPaymentMethods()}
                                    />
                                )}
                                
                                {
                                    !showPaymentMemo && <a margin='0px 0px' onClick={() => this.setState({ showPaymentMemo: true })} text={'Add payment memo'} leftIcon={<LucidePlus />}><Flex start centerH gap='8px'>
                                        <LucidePlus size='16px' color={hexToRgba(theme.colors.primary, 0.9)} />
                                        <Text color={hexToRgba(theme.colors.primary, 0.9)}>Add payment memo</Text>
                                    </Flex></a>
                                }
                                {
                                    showPaymentMemo &&
                                    <>
                                        <Flex gap='24px'>
                                            {/* Payment Identifier */}
                                            <LabeledInput
                                                id="paymentDesc"
                                                label="Payment identifier"
                                                optional
                                                nomargin
                                                key="paymentDesc"
                                                value={paymentDesc}
                                                maxLength={identifierMaxLength}
                                                instruction={`Identifier to help your vendor match this payment ${paymentDesc ? `(${paymentDesc.length}/${identifierMaxLength} characters)` : ''}`}
                                                placeholder={`Add up to ${identifierMaxLength} characters`}
                                                onChange={this.handleBillTextChange}
                                                errorField={errorField}
                                                error={errorField === "paymentDesc"}
                                                errorMessage="Add a description"
                                            />

                                            {/* Payment Memo */}
                                            <LabeledInput
                                                id="paymentMemo"
                                                label="Payment memo"
                                                type="text-area"
                                                optional
                                                nomargin
                                                key="paymentMemo"
                                                value={paymentMemo}
                                                autoSize={{ minRows: 1, maxRows: 3 }}
                                                maxLength={memoMaxLength}
                                                instruction={`Memo to help identify this payment's purpose ${paymentMemo ? `(${paymentMemo.length}/${memoMaxLength} characters)` : ''}`}
                                                placeholder={`Add up to ${memoMaxLength} characters`}
                                                onChange={this.handleBillTextChange}
                                                errorField={errorField}
                                                error={errorField === "paymentMemo"}
                                                errorMessage="Add a valid memo"
                                            />
                                        </Flex>
                                    </>
                                }

                                {/* Approvals Section */}
                                {approvalRequired === true && (
                                    <>
                                        <div style={{ height: 1, width: '100%', background: '#F2F2F2' }}></div>
                                        {
                                            expandApprovals ?
                                            <FlexColumn start>
                                                <Flex start between centerH>
                                                    <Text size='16px' weight={600}>Approvals required</Text>
                                                    <a style={{ textDecoration: "underline", color: hexToRgba(theme.colors.primary2, 0.6), fontSize: '14px' }} onClick={() => this.setState({ expandApprovals: false })}>Hide</a>
                                                </Flex>
                                                
                                                <ApprovalGraph rule={approvalRule} />
                                            </FlexColumn>
                                            :
                                            <Flex start between centerH>
                                                <Text size='16px' weight={600}>Approvals required</Text>
                                                <div style={{ cursor: 'pointer' }} onClick={() => { this.setState({ expandApprovals: true }) }}>
                                                    <ApprovalGraph margin='0px' miniVersion rule={approvalRule} />
                                                </div>
                                            </Flex>
                                        }
                                    </>
                                )}
                            </>
                        </FlexColumn>
                    </FlexColumn>

                    {/* Footer with Pay/Schedule Button */}
                    <Flex start gap="16px" style={{ padding: '24px' }}>
                        <Button
                            text={approvalRequired ? "SEND FOR APPROVAL" : (this.isPaymentDateToday() ? "PAY" : "SCHEDULE")}
                            onClick={this.pay}
                            loading={payBillLoading || submitLoading}
                            primary
                            solid
                        />
                    </Flex>
                </FlexColumn>

                {/* Payment Method Modal */}
                <Modal
                    visible={this.state.showPaymentModal}
                    footer={null}
                    closable={true}
                    width={700}
                    destroyOnClose={true}
                    onCancel={() => this.setState({ showPaymentModal: false, editPm: null })}
                    closeIcon={<img width='24px' height='24px' src={ModalClose} />}
                >
                    <AddPaymentMethod
                        vendor={vendor}
                        onSave={(updatedVendor) => {
                            this.setState({ showPaymentModal: false, vendor: updatedVendor }, () => {
                                this.getPaymentMethods({ preselect: true });
                            });
                        }}
                    />
                </Modal>
            </FlexColumn>
        );
    }
}

export default connect(
    state => ({
        store: state.aionAppReducer,
        bankingStore: state.bankingAppReducer
    })
)(withTheme(PayCard));